/**
 * Permissions élémentaires. Les rôles en sont des regroupements ; un rôle personnalisé
 * peut être créé par l'administrateur d'une entreprise.
 */
export const PERMISSIONS = {
  'company:manage': "Gérer les informations de l'entreprise",
  'user:read': 'Consulter les utilisateurs',
  'user:manage': 'Créer, modifier, désactiver des utilisateurs et leurs rôles',
  'audit:read': "Consulter le journal d'audit",
  'project:read': 'Consulter les projets',
  'project:create': 'Créer des projets',
  'project:edit': 'Modifier les projets, la structure et le métré',
  'project:delete': 'Archiver ou supprimer des projets',
  'catalog:read': 'Consulter les catalogues (matériaux, matériels, main-d\'œuvre, recettes)',
  'catalog:edit': 'Modifier les catalogues',
  'price:edit': 'Modifier les prix',
  'recipe:edit': 'Modifier les recettes et coefficients (béton, maçonnerie, acier…)',
  'family:edit': "Créer et modifier les familles d'ouvrages et leurs formules",
  'quotation:read': 'Consulter les devis',
  'quotation:edit': 'Préparer les devis',
  'quotation:finalize': 'Finaliser, versionner et exporter les devis',
  'invoice:read': 'Consulter les factures et les paiements',
  'invoice:edit': 'Préparer les factures, décomptes et avoirs',
  'invoice:validate': 'Valider les factures, décomptes et avoirs (numéro définitif, plus de modification)',
  'payment:record': 'Enregistrer et annuler les paiements',
  'validation:approve': "Valider en tant qu'ingénieur (🟢 Validé par l'ingénieur)",
  'ai:use': 'Utiliser ExpertDevis AI',
  'settings:manage': 'Gérer les paramètres (unités, devises, taxes, localisation)',
} as const;

export type Permission = keyof typeof PERMISSIONS;
export const ALL_PERMISSIONS = Object.keys(PERMISSIONS) as Permission[];

export const SYSTEM_ROLES = {
  ADMIN: {
    name: 'Administrateur',
    description: "Accès complet à l'entreprise",
    permissions: ALL_PERMISSIONS,
  },
  DIRECTOR: {
    name: 'Directeur',
    description: "Pilote toute l'activité : études, devis, factures et paiements ; ne gère ni les comptes ni la validation technique",
    permissions: [
      'user:read', 'audit:read', 'project:read', 'project:create', 'project:edit', 'project:delete',
      'catalog:read', 'catalog:edit', 'price:edit',
      'quotation:read', 'quotation:edit', 'quotation:finalize', 'invoice:read', 'invoice:edit', 'invoice:validate', 'payment:record', 'ai:use',
    ],
  },
  ENGINEER: {
    name: 'Ingénieur',
    description: 'Conçoit, chiffre et valide les études',
    permissions: [
      'user:read', 'audit:read', 'project:read', 'project:create', 'project:edit', 'project:delete',
      'catalog:read', 'catalog:edit', 'price:edit', 'recipe:edit', 'family:edit',
      'quotation:read', 'quotation:edit', 'quotation:finalize', 'invoice:read', 'validation:approve', 'ai:use',
    ],
  },
  QUANTITY_SURVEYOR: {
    name: 'Métreur',
    description: 'Saisit le métré et prépare les devis',
    permissions: [
      'project:read', 'project:create', 'project:edit', 'catalog:read', 'quotation:read', 'quotation:edit', 'ai:use',
    ],
  },
  MANAGER: {
    name: 'Gestionnaire',
    description: 'Gère les prix, les catalogues et les devis',
    permissions: [
      'user:read', 'audit:read', 'project:read', 'catalog:read', 'catalog:edit', 'price:edit',
      'quotation:read', 'quotation:edit', 'quotation:finalize', 'invoice:read', 'invoice:edit', 'invoice:validate', 'payment:record', 'ai:use',
    ],
  },
  ACCOUNTANT: {
    name: 'Comptable',
    description: 'Facture, valide les pièces comptables et enregistre les paiements',
    permissions: ['audit:read', 'project:read', 'catalog:read', 'quotation:read', 'invoice:read', 'invoice:edit', 'invoice:validate', 'payment:record'],
  },
  COMMERCIAL: {
    name: 'Commercial',
    description: 'Prépare et suit les devis et les clients',
    permissions: ['project:read', 'project:create', 'project:edit', 'catalog:read', 'quotation:read', 'quotation:edit', 'quotation:finalize', 'invoice:read', 'ai:use'],
  },
  VIEWER: {
    name: 'Lecteur',
    description: 'Lecture seule',
    permissions: ['project:read', 'catalog:read', 'quotation:read', 'invoice:read'],
  },
} as const satisfies Record<string, { name: string; description: string; permissions: readonly Permission[] }>;

export type SystemRoleCode = keyof typeof SYSTEM_ROLES;
